import { PageHeading } from "@/components/ui/page";
import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Save, User, Building2, KeyRound } from "lucide-react";
import { usersAPI, rolesAPI, branchesAPI, authAPI, permissionsAPI, getApiErrorPayload } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useToast } from "@/hooks/use-toast";
import { usePermission } from "@/hooks/usePermission";
import { Loading } from "@/components/ui/loader";
import { Badge } from "@/components/ui/badge";
import { GroupedPermissionsPanel } from "@/components/permissions/GroupedPermissionsPanel";
import { EmployeeBranchPosSection } from "@/features/users/components/EmployeeBranchPosSection";
import { confirmAction } from "@/components/ui/confirm-dialog";
import { useUnsavedChangesGuard, useIsDirty } from "@/hooks/useUnsavedChangesGuard";
import { isValidSaudiMobile } from "@/lib/saudi-phone";
import { visibleEmployeeEmail } from "@/lib/employee-email";

interface Permission {
	id: number;
	name: string;
	nameEn?: string;
	action: string;
	description: string;
	isActive: boolean;
	type: string;
}

interface Employee {
	id: number | string;
	fullName: string;
	email: string;
	phone: string;
	birthDate: string;
	joinDate: string;
	workHoursFrom: string;
	workHoursTo: string;
	address: string;
	photo?: string;
	password?: string;
	confirmPassword?: string;
	roleId: number;
	branchId?: number;
	isActive: boolean;
	createdAt: string;
	updatedAt: string;
	hasPosPin?: boolean;
	role?: {
		id: number;
		name: string;
		permissions?: Permission[];
	};
	extraPermissions?: Permission[];
	branch?: {
		id: number;
		name: string;
	};
}

interface Role {
	id: number;
	name: string;
	status: string;
	permissions?: Permission[];
}

interface Branch {
	id: number;
	name: string;
	status: string;
}

const EmployeeDetailPage: React.FC = () => {
	const { t } = useTranslation();
	const { id } = useParams<{ id: string }>();
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();
	// Set by the create page: the new employee still needs a PIN and a POS point for cashier sign-in.
	const isPinSetup = searchParams.get("setup") === "pin";
	const { toast } = useToast();
	const [activeTab, setActiveTab] = useState("personal");
	const [loading, setLoading] = useState(false);
	const [loaded, setLoaded] = useState(!id);
	const [saving, setSaving] = useState(false);
	const [roles, setRoles] = useState<Role[]>([]);
	const [branches, setBranches] = useState<Branch[]>([]);
	const [posPin, setPosPin] = useState("");
	const [posPinConfirm, setPosPinConfirm] = useState("");
	const [pinActionLoading, setPinActionLoading] = useState(false);
	const [pinEditorOpen, setPinEditorOpen] = useState(false);
	const [posPinLockedUntil, setPosPinLockedUntil] = useState<number | null>(null);
	const [allPermissionsList, setAllPermissionsList] = useState<Permission[]>([]);
	const [extraPermissionIds, setExtraPermissionIds] = useState<number[]>([]);
	const [posIds, setPosIds] = useState<number[]>([]);
	const [defaultPosId, setDefaultPosId] = useState<number | null>(null);
	const [employee, setEmployee] = useState<Employee>({
		id: 0,
		fullName: "",
		email: "",
		phone: "",
		birthDate: "",
		joinDate: "",
		workHoursFrom: "",
		workHoursTo: "",
		address: "",
		photo: "",
		password: "",
		confirmPassword: "",
		roleId: 0,
		branchId: undefined,
		isActive: true,
		createdAt: "",
		updatedAt: "",
	});

	const isEditing = !!id;
	const canEdit = usePermission("users.update");
	const isViewOnly = isEditing && !canEdit;
	const canManagePosPin = isEditing && canEdit;
	const isPosPinLocked = posPinLockedUntil !== null && posPinLockedUntil > Date.now();

	useEffect(() => {
		if (isPinSetup && canManagePosPin) setActiveTab("posPin");
	}, [isPinSetup, canManagePosPin]);

	// Only fields saved by "Save" count; the photo and PIN are stored as soon as they change.
	const {
		photo: _photo,
		hasPosPin: _hasPosPin,
		role: _role,
		branch: _branch,
		extraPermissions: _extraPermissions,
		createdAt: _createdAt,
		updatedAt: _updatedAt,
		...editableEmployee
	} = employee;
	const { isDirty, markSaved } = useIsDirty({ editableEmployee, extraPermissionIds, posIds, defaultPosId }, loaded);
	const { allowNavigation } = useUnsavedChangesGuard(isDirty && !saving);

	useEffect(() => {
		if (posPinLockedUntil === null) return;
		const remaining = posPinLockedUntil - Date.now();
		if (remaining <= 0) {
			setPosPinLockedUntil(null);
			return;
		}
		const timer = window.setTimeout(() => setPosPinLockedUntil(null), remaining);
		return () => window.clearTimeout(timer);
	}, [posPinLockedUntil]);

	useEffect(() => {
		fetchRoles();
		fetchBranches();
		const loadPerms = async () => {
			try {
				const data = await permissionsAPI.getAll();
				setAllPermissionsList(Array.isArray(data) ? data : []);
			} catch {
				/* optional */
			}
		};
		loadPerms();
		if (isEditing) {
			fetchEmployee();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps -- fetch on id change only
	}, [id]);

	const fetchRoles = async () => {
		try {
			const data = await rolesAPI.getAll();
			setRoles(data);
		} catch (error) {
			toast({
				title: t("common.error"),
				description: t("users.errors.fetchRolesFailed"),
				variant: "destructive",
			});
		}
	};

	const fetchBranches = async () => {
		try {
			const data = await branchesAPI.getAll();
			setBranches(data);
		} catch (error) {
			toast({
				title: t("common.error"),
				description: t("users.errors.fetchBranchesFailed"),
				variant: "destructive",
			});
		}
	};

	const fetchEmployee = async () => {
		try {
			setLoading(true);
			const [data, pinStatus] = await Promise.all([
				usersAPI.getById(id!),
				canEdit ? authAPI.getPosPinStatus(Number(id)) : Promise.resolve({ locked: false, lockedUntil: null }),
			]);

			const formatDate = (dateString: string | null | undefined): string => {
				if (!dateString) return "";
				const date = new Date(dateString);
				if (isNaN(date.getTime())) return "";
				return date.toISOString().split("T")[0];
			};

			const splitWorkHours = (
				workHoursFrom: string | null | undefined,
				workHoursTo: string | null | undefined,
			): { from: string; to: string } => {
				if (!workHoursFrom || !workHoursTo) return { from: "", to: "" };
				return { from: workHoursFrom, to: workHoursTo };
			};

			// Map status to isActive
			const isActive = data.status === "active";

			// Map work hours from and to
			const workHoursSplit = splitWorkHours(data.workHoursFrom, data.workHoursTo);

			setEmployee({
				id: data.id || "",
				fullName: data.fullName || [data.firstName, data.lastName].filter(Boolean).join(" "),
				email: visibleEmployeeEmail(data.email),
				phone: data.phone || "",
				birthDate: formatDate(data.birthDate),
				joinDate: formatDate(data.joinDate),
				workHoursFrom: workHoursSplit.from,
				workHoursTo: workHoursSplit.to,
				address: data.address || "",
				photo: data.photo || "",
				password: "",
				confirmPassword: "",
				roleId: data.roleId || data.role?.id || "",
				branchId: data.branchId || data.branch?.id || "",
				isActive: isActive,
				createdAt: data.createdAt || "",
				updatedAt: data.updatedAt || "",
				hasPosPin: data.hasPosPin,
				role: data.role,
				branch: data.branch,
				extraPermissions: data.extraPermissions,
			});
			setExtraPermissionIds((data.extraPermissions ?? []).map((p: Permission) => Number(p.id)));
			setPosIds(Array.isArray(data.posIds) ? data.posIds.map((n: number) => Number(n)) : []);
			setDefaultPosId(data.defaultPosId != null ? Number(data.defaultPosId) : null);
			setPinEditorOpen(false);
			setPosPin("");
			setPosPinConfirm("");
			setPosPinLockedUntil(pinStatus.locked ? pinStatus.lockedUntil : null);
			setLoaded(true);
		} catch (error) {
			toast({
				title: t("common.error"),
				description: t("users.errors.fetchFailed"),
				variant: "destructive",
			});
			navigate("/users");
		} finally {
			setLoading(false);
		}
	};

	const handleInputChange = (field: keyof Employee, value: any) => {
		setEmployee(prev => ({ ...prev, [field]: value }));
	};

	const handlePhotoUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
		const file = event.target.files?.[0];
		if (file) {
			try {
				// Validate file size (5MB max)
				if (file.size > 5 * 1024 * 1024) {
					toast({
						title: t("common.error"),
						description: t("users.errors.fileTooLarge") || "File size must be less than 5MB",
						variant: "destructive",
					});
					return;
				}

				// Validate file type
				if (!file.type.match(/^image\/(jpg|jpeg|png|gif)$/)) {
					toast({
						title: t("common.error"),
						description:
							t("users.errors.invalidFileType") || "Only image files (JPG, PNG, GIF) are allowed",
						variant: "destructive",
					});
					return;
				}

				// Show preview immediately
				const reader = new FileReader();
				reader.onload = e => {
					setEmployee(prev => ({
						...prev,
						photo: e.target?.result as string,
					}));
				};
				reader.readAsDataURL(file);

				const uploadResult = await usersAPI.uploadPhoto(file, isEditing ? id : undefined);

				if (!uploadResult || !uploadResult.url) {
					throw new Error("Upload failed: No URL returned");
				}

				const baseUrl = (process.env.REACT_APP_API_URL || "http://localhost:3000/api").replace("/api", "");
				const photoUrl = uploadResult.url.startsWith("http")
					? uploadResult.url
					: `${baseUrl}${uploadResult.url}`;

				setEmployee(prev => ({
					...prev,
					photo: photoUrl,
				}));

				toast({
					title: t("common.success"),
					description: t("users.messages.photoUploaded"),
				});
			} catch (error) {
				const payload = getApiErrorPayload(error);
				const errorMessage = t(payload.key, (payload.params ?? {}) as Record<string, string>);
				toast({
					title: t("common.error"),
					description: errorMessage || t("users.errors.photoUploadFailed") || "Failed to upload photo",
					variant: "destructive",
				});
				// Clear the photo on error
				setEmployee(prev => ({
					...prev,
					photo: "",
				}));
			}
		}
	};

	const handleSubmit = async () => {
		try {
			setSaving(true);

			// Validate password confirmation
			if (!isEditing && employee.password !== employee.confirmPassword) {
				toast({
					title: t("common.error"),
					description: t("users.errors.passwordMismatch"),
					variant: "destructive",
				});
				return;
			}

			// The mobile is the employee's sign-in identifier.
			const phoneError = !employee.phone?.trim()
				? "users.errors.phoneRequired"
				: !isValidSaudiMobile(employee.phone)
					? "users.errors.phoneInvalid"
					: null;
			if (phoneError) {
				toast({ title: t("common.error"), description: t(phoneError), variant: "destructive" });
				return;
			}

			const payload: any = {
				fullName: employee.fullName,
				phone: employee.phone,
				birthDate: employee.birthDate,
				address: employee.address,
				photo: employee.photo,
				joinDate: employee.joinDate,
				branchId: employee.branchId,
				roleId: employee.roleId,
				posIds,
				defaultPosId,
			};

			// Employees created without an email keep their generated address unless one is entered.
			if (employee.email) payload.email = employee.email;

			if (employee.password && employee.password.trim().length > 0) {
				payload.password = employee.password;
			}

			if (employee.workHoursFrom && employee.workHoursTo) {
				payload.workHoursFrom = employee.workHoursFrom;
				payload.workHoursTo = employee.workHoursTo;
			}

			// Map isActive to status
			payload.status = employee.isActive ? "active" : "inactive";

			if (isEditing) {
				payload.extraPermissionIds = extraPermissionIds;
				await usersAPI.update(id!, payload);
				markSaved();
				toast({
					title: t("common.success"),
					description: t("users.messages.updated"),
				});
			} else {
				await usersAPI.create(payload);
				toast({
					title: t("common.success"),
					description: t("users.messages.created"),
				});
				allowNavigation();
				navigate("/users");
			}
		} catch (error) {
			const payload = getApiErrorPayload(error);
			const errorMessage = t(payload.key, (payload.params ?? {}) as Record<string, string>);
			toast({
				title: t("common.error"),
				description: errorMessage || t("common.errors.unexpected"),
				variant: "destructive",
			});
		} finally {
			setSaving(false);
		}
	};

	const tabList = canManagePosPin ? ["personal", "work", "posPin"] : ["personal", "work"];
	const nextTab = () => {
		const currentIndex = tabList.indexOf(activeTab);
		if (currentIndex < tabList.length - 1) {
			setActiveTab(tabList[currentIndex + 1]);
		}
	};

	const previousTab = () => {
		const currentIndex = tabList.indexOf(activeTab);
		if (currentIndex > 0) {
			setActiveTab(tabList[currentIndex - 1]);
		}
	};

	const handleSetPosPin = async () => {
		if (!id) return;
		if (!/^\d{4}$/.test(posPin)) {
			toast({
				title: t("common.error"),
				description: t("auth.errors.pinExactLength"),
				variant: "destructive",
			});
			return;
		}
		if (posPin !== posPinConfirm) {
			toast({
				title: t("common.error"),
				description: t("users.posPin.pinMismatch"),
				variant: "destructive",
			});
			return;
		}
		try {
			setPinActionLoading(true);
			await usersAPI.setPosPin(id, posPin);
			toast({ title: t("common.success"), description: t("users.posPin.setSuccess") });
			setPosPin("");
			setPosPinConfirm("");
			setPinEditorOpen(false);
			await fetchEmployee();
		} catch (error) {
			const payload = getApiErrorPayload(error);
			toast({
				title: t("common.error"),
				description: t(payload.key, (payload.params ?? {}) as Record<string, string>),
				variant: "destructive",
			});
		} finally {
			setPinActionLoading(false);
		}
	};

	const handleClearPosPin = async () => {
		if (!id) return;
		if (!(await confirmAction(t("users.posPin.clearConfirm")))) return;
		try {
			setPinActionLoading(true);
			await usersAPI.setPosPin(id, "");
			toast({ title: t("common.success"), description: t("users.posPin.clearSuccess") });
			setPinEditorOpen(false);
			await fetchEmployee();
		} catch (error) {
			const payload = getApiErrorPayload(error);
			toast({
				title: t("common.error"),
				description: t(payload.key, (payload.params ?? {}) as Record<string, string>),
				variant: "destructive",
			});
		} finally {
			setPinActionLoading(false);
		}
	};

	const handleUnlockPosPin = async () => {
		if (!id) return;
		const userId = typeof employee.id === "string" ? parseInt(employee.id, 10) : employee.id;
		try {
			setPinActionLoading(true);
			await authAPI.unlockPosPin(userId);
			setPosPinLockedUntil(null);
			toast({ title: t("common.success"), description: t("users.posPin.unlockSuccess") });
		} catch (error) {
			const payload = getApiErrorPayload(error);
			toast({
				title: t("common.error"),
				description: t(payload.key, (payload.params ?? {}) as Record<string, string>),
				variant: "destructive",
			});
		} finally {
			setPinActionLoading(false);
		}
	};

	if (loading) return <Loading />;

	return (
		<div className="container mx-auto py-6 space-y-6">
			<div className="flex items-center justify-between">
				<div className="flex items-center gap-4">
					<div>
						<PageHeading className="mb-2 ">
							{isViewOnly
								? t("common.view")
								: isEditing
									? t("users.actions.edit")
									: t("users.actions.create")}
						</PageHeading>
						<p className="text-muted-foreground">
							{isViewOnly
								? t("users.form.viewDescription")
								: isEditing
									? t("users.form.editDescription")
									: t("users.form.createDescription")}
						</p>
					</div>
				</div>
				{!isViewOnly && (
					<Button onClick={handleSubmit} disabled={saving}>
						{saving ? (
							<div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white me-2"></div>
						) : (
							<Save className="h-4 w-4 mx-2" />
						)}
						{t("common.save")}
					</Button>
				)}
			</div>

			{isPinSetup && (
				<div role="status" className="rounded-md border border-primary/30 bg-primary/5 p-3 text-sm">
					{t("users.notices.pinSetup")}
				</div>
			)}

			<Tabs value={activeTab} onValueChange={setActiveTab}>
				<TabsList className="bg-slate-200 h-14 p-2">
					<TabsTrigger value="personal" className="flex items-center gap-2 px-4">
						<User className="h-4 w-4" />
						{t("users.tabs.personalInfo")}
					</TabsTrigger>
					<TabsTrigger value="work" className="flex items-center gap-2 px-4">
						<Building2 className="h-4 w-4" />
						{t("users.tabs.workInfo")}
					</TabsTrigger>
					{canManagePosPin && (
						<TabsTrigger value="posPin" className="flex items-center gap-2 px-4">
							<KeyRound className="h-4 w-4" />
							{t("users.tabs.posPin")}
						</TabsTrigger>
					)}
				</TabsList>

				{/* Personal Information Tab */}
				<TabsContent value="personal" className="space-y-6 bg-card rounded-lg p-2">
					<Card>
						<CardHeader>
							<CardTitle>{t("users.tabs.personalInfo")}</CardTitle>
							<CardDescription>{t("users.form.personalInfoDescription")}</CardDescription>
						</CardHeader>
						<CardContent className="space-y-6">
							{/* Photo Upload */}
							<div className="space-y-2">
								<Label>{t("users.form.photo")}</Label>
								<div className="flex items-center gap-4">
									{employee.photo && (
										<img
											src={employee.photo}
											alt={employee.fullName}
											className="w-24 h-24 object-cover rounded-lg border"
										/>
									)}
									{!isViewOnly && (
										<div>
											<Input
												aria-label={t("users.form.photo")}
												type="file"
												accept="image/*"
												onChange={handlePhotoUpload}
												className="w-64"
											/>
											<p className="text-sm text-muted-foreground mt-1">
												{t("users.form.photoHelp")}
											</p>
										</div>
									)}
								</div>
							</div>

							{/* Basic Information */}
							<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
								<div className="space-y-2">
									<Label htmlFor="fullName">{t("users.form.fullName")}</Label>
									<Input
										id="fullName"
										value={employee.fullName}
										onChange={e => handleInputChange("fullName", e.target.value)}
										placeholder={t("users.form.fullNamePlaceholder")}
										readOnly={isViewOnly}
										className={isViewOnly ? "bg-muted" : ""}
									/>
								</div>

								<div className="space-y-2">
									<Label htmlFor="email">{t("users.form.email")}</Label>
									<Input
										id="email"
										type="email"
										value={employee.email}
										onChange={e => handleInputChange("email", e.target.value)}
										placeholder={t("users.form.emailPlaceholder")}
										readOnly={isViewOnly}
										className={isViewOnly ? "bg-muted" : ""}
									/>
								</div>

								<div className="space-y-2">
									<Label htmlFor="phone">
										{t("users.form.phone")}
										{!isViewOnly && <span className="text-destructive"> *</span>}
									</Label>
									<Input
										id="phone"
										value={employee.phone}
										onChange={e => handleInputChange("phone", e.target.value)}
										placeholder={t("users.form.phonePlaceholder")}
										className={`rtl:text-right ${isViewOnly ? "bg-muted" : ""}`}
										dir="ltr"
										readOnly={isViewOnly}
									/>
								</div>

								<div className="space-y-2">
									<Label htmlFor="birthDate">{t("users.form.birthDate")}</Label>
									<Input
										id="birthDate"
										type="date"
										value={employee.birthDate}
										onChange={e => handleInputChange("birthDate", e.target.value)}
										readOnly={isViewOnly}
										className={isViewOnly ? "bg-muted" : ""}
									/>
								</div>

								<div className="space-y-2">
									<Label htmlFor="address">{t("users.form.address")}</Label>
									<Input
										id="address"
										type="text"
										value={employee.address}
										onChange={e => handleInputChange("address", e.target.value)}
										placeholder={t("users.form.addressPlaceholder")}
										readOnly={isViewOnly}
										className={isViewOnly ? "bg-muted" : ""}
									/>
								</div>
							</div>

							{/* Account section (merged from the former Account Information tab) */}
							<div className="space-y-4 pt-4 border-t border-border/60">
								<div>
									<Label className="text-base">{t("users.tabs.accountInfo")}</Label>
									<p className="text-sm text-muted-foreground mt-1">
										{t("users.form.accountInfoDescription")}
									</p>
								</div>
								<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
									<div className="space-y-2">
										<Label htmlFor="password">{t("users.form.password")}</Label>
										<Input
											id="password"
											type="password"
											value={employee.password}
											onChange={e => handleInputChange("password", e.target.value)}
											placeholder={
												isEditing
													? t("users.form.passwordEditPlaceholder")
													: t("users.form.passwordPlaceholder")
											}
											readOnly={isViewOnly}
											className={isViewOnly ? "bg-muted" : ""}
										/>
									</div>
									{!isEditing && (
										<div className="space-y-2">
											<Label htmlFor="confirmPassword">{t("users.form.confirmPassword")}</Label>
											<Input
												id="confirmPassword"
												type="password"
												value={employee.confirmPassword}
												onChange={e => handleInputChange("confirmPassword", e.target.value)}
												placeholder={t("users.form.confirmPasswordPlaceholder")}
												readOnly={isViewOnly}
												className={isViewOnly ? "bg-muted" : ""}
											/>
										</div>
									)}

									<div className="space-y-2">
										<Label htmlFor="isActive">{t("users.form.isActive")}</Label>
										<div className="flex items-center justify-evenly border border-border rounded-md p-3">
											<Label htmlFor="isActive" className="w-full text-center">
												{t("users.form.active")}
											</Label>
											<Switch
												id="isActive"
												checked={employee.isActive}
												onCheckedChange={checked => handleInputChange("isActive", checked)}
												disabled={isViewOnly}
											/>
											<Label htmlFor="isActive" className="w-full text-center">
												{t("users.form.inactive")}
											</Label>
										</div>
									</div>
								</div>
							</div>

							{!isViewOnly && (
								<div className="flex justify-end">
									<Button
										onClick={nextTab}
										className="px-12"
										disabled={!employee.fullName || (!isEditing && !employee.password)}
									>
										{t("common.next")}
									</Button>
								</div>
							)}
						</CardContent>
					</Card>
				</TabsContent>

				{/* Work Information Tab */}
				<TabsContent value="work" className="space-y-6 bg-card rounded-lg p-2">
					<Card>
						<CardHeader>
							<CardTitle>{t("users.tabs.workInfo")}</CardTitle>
							<CardDescription>{t("users.form.workInfoDescription")}</CardDescription>
						</CardHeader>
						<CardContent className="space-y-6">
							<div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
								<div className="space-y-2">
									<Label htmlFor="joinDate">{t("users.form.joinDate")}</Label>
									<Input
										id="joinDate"
										type="date"
										value={employee.joinDate}
										onChange={e => handleInputChange("joinDate", e.target.value)}
										readOnly={isViewOnly}
										className={isViewOnly ? "bg-muted" : ""}
									/>
								</div>
								<div className="space-y-2">
									<Label htmlFor="workHoursFrom">{t("users.form.workHoursFrom")}</Label>
									<Input
										id="workHoursFrom"
										type="time"
										value={employee.workHoursFrom}
										onChange={e => handleInputChange("workHoursFrom", e.target.value)}
										readOnly={isViewOnly}
										className={isViewOnly ? "bg-muted" : ""}
									/>
								</div>
								<div className="space-y-2">
									<Label htmlFor="workHoursTo">{t("users.form.workHoursTo")}</Label>
									<Input
										id="workHoursTo"
										type="time"
										value={employee.workHoursTo}
										onChange={e => handleInputChange("workHoursTo", e.target.value)}
										readOnly={isViewOnly}
										className={isViewOnly ? "bg-muted" : ""}
									/>
								</div>
								<div className="space-y-2">
									<Label htmlFor="roleId">{t("users.form.role")}</Label>
									<Select
										value={
											employee.roleId != null && employee.roleId !== 0
												? String(employee.roleId)
												: ""
										}
										onValueChange={value => handleInputChange("roleId", value ? Number(value) : 0)}
										disabled={isViewOnly}
									>
										<SelectTrigger
											aria-label={t("users.form.role")}
											className={isViewOnly ? "bg-muted" : ""}
										>
											<SelectValue placeholder={t("users.form.selectRole")} />
										</SelectTrigger>
										<SelectContent>
											{roles.map(role => (
												<SelectItem key={role.id} value={String(role.id)}>
													{role.name}
												</SelectItem>
											))}
										</SelectContent>
									</Select>
								</div>
								<EmployeeBranchPosSection
									branches={branches}
									branchId={employee.branchId}
									onBranchChange={value => handleInputChange("branchId", value)}
									onBranchesRefresh={() => fetchBranches()}
									posIds={posIds}
									onPosIdsChange={setPosIds}
									defaultPosId={defaultPosId}
									onDefaultPosChange={setDefaultPosId}
									disabled={isViewOnly}
								/>
							</div>

							{/* Role Permissions Display */}
							{employee.roleId ? (
								<div className="space-y-2">
									<div>
										<Label>
											{t("users.form.permissionsFor")}{" "}
											{roles.find(role => role.id === employee.roleId)?.name}
										</Label>
										<p className="text-sm text-muted-foreground mt-1">
											{t("permissions.ui.effectivePermissionsHint")}
										</p>
									</div>
									{roles.find(role => role.id === employee.roleId)?.permissions?.length ? (
										<GroupedPermissionsPanel
											permissions={
												roles.find(role => role.id === employee.roleId)?.permissions ?? []
											}
											idPrefix="user-eff"
											showSearch
											readonlyGranted
											scrollAreaClassName="overflow-visible overscroll-contain"
										/>
									) : (
										<p className="text-sm text-muted-foreground border rounded-md p-4">
											{t("users.form.noPermissionsAvailable")}
										</p>
									)}
								</div>
							) : null}

							{isEditing && canEdit && allPermissionsList.length > 0 ? (
								<div className="space-y-2 pt-4 border-t border-border/60">
									<div className="flex items-center gap-2 flex-wrap">
										<Label>{t("users.form.additionalPermissions")}</Label>
										<Badge variant="secondary">{extraPermissionIds.length}</Badge>
									</div>
									<p className="text-sm text-muted-foreground">
										{t("users.form.additionalPermissionsHint")}
									</p>
									<GroupedPermissionsPanel
										permissions={allPermissionsList}
										idPrefix="user-extra"
										showSearch
										selection={{
											selectedIds: extraPermissionIds.map(String),
											onToggle: (permId, checked) => {
												const n = Number(permId);
												setExtraPermissionIds(prev => {
													if (checked) {
														return prev.includes(n) ? prev : [...prev, n];
													}
													return prev.filter(x => x !== n);
												});
											},
										}}
										scrollAreaClassName="max-h-[min(420px,55vh)] overflow-y-auto overscroll-contain rounded-md border border-border/60 p-2"
									/>
								</div>
							) : null}

							{!isViewOnly && (
								<div className="flex justify-between mt-4 pt-4">
									<Button className="px-12" variant="outline" onClick={previousTab}>
										{t("common.previous")}
									</Button>
									<Button
										className="px-12"
										onClick={handleSubmit}
										disabled={saving || !employee.roleId || !employee.branchId}
									>
										{saving ? (
											<div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white me-2"></div>
										) : (
											<Save className="h-4 w-4 mx-2" />
										)}
										{t("common.save")}
									</Button>
								</div>
							)}
							{isViewOnly && (
								<div className="flex justify-end mt-4 pt-4">
									<Button className="px-12" variant="outline" onClick={previousTab}>
										{t("common.previous")}
									</Button>
								</div>
							)}
						</CardContent>
					</Card>
				</TabsContent>

				{/* POS PIN Tab */}
				{canManagePosPin && (
					<TabsContent value="posPin" className="space-y-6 bg-card rounded-lg p-2">
						<Card>
							<CardHeader className="flex flex-row justify-between">
								<div className="space-y-2">
									<CardTitle>{t("users.tabs.posPin")}</CardTitle>
								</div>
							</CardHeader>
							<CardContent className="space-y-6">
								{!pinEditorOpen ? (
									<div className="space-y-4">
										{employee.hasPosPin ? (
											<p className="text-sm font-medium w-full max-w-80 p-4 bg-success/10 rounded-sm">
												{t("users.posPin.statusSet")}
											</p>
										) : (
											<p className="text-sm font-medium w-full max-w-80 p-4 bg-destructive/10 rounded-sm">
												{t("users.posPin.statusNotSet")}
											</p>
										)}
										{employee.hasPosPin ? (
											<div className="flex flex-wrap gap-2">
												<Button
													className="min-w-40"
													onClick={() => setPinEditorOpen(true)}
													disabled={pinActionLoading}
												>
													{t("users.posPin.changePin")}
												</Button>
												<Button
													className="min-w-40"
													onClick={handleClearPosPin}
													variant="destructive"
													disabled={pinActionLoading}
												>
													{t("users.posPin.clearPin")}
												</Button>
											</div>
										) : (
											<Button onClick={() => setPinEditorOpen(true)} disabled={pinActionLoading}>
												{t("users.posPin.setPinCode")}
											</Button>
										)}
									</div>
								) : (
									<div className="space-y-4">
										<div className="flex flex-col gap-4 sm:flex-row">
											<div className="space-y-2 w-full max-w-64">
												<Label htmlFor="posPin">{t("users.posPin.newPin")}</Label>
												<Input
													id="posPin"
													type="password"
													inputMode="numeric"
													autoComplete="off"
													value={posPin}
													onChange={e => setPosPin(e.target.value.replace(/\D/g, ""))}
													maxLength={4}
													className="font-mono"
												/>
											</div>
											<div className="space-y-2 w-full max-w-64">
												<Label htmlFor="posPinConfirm">{t("users.posPin.confirmPin")}</Label>
												<Input
													id="posPinConfirm"
													type="password"
													inputMode="numeric"
													autoComplete="off"
													value={posPinConfirm}
													onChange={e => setPosPinConfirm(e.target.value.replace(/\D/g, ""))}
													maxLength={4}
													className="font-mono"
												/>
											</div>
										</div>
										<Button
											onClick={handleSetPosPin}
											className="w-full max-w-64"
											disabled={
												pinActionLoading || posPin.length !== 4 || posPin !== posPinConfirm
											}
										>
											{pinActionLoading ? (
												<div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white me-2" />
											) : null}
											{employee.hasPosPin
												? t("users.posPin.changePin")
												: t("users.posPin.setPin")}
										</Button>
									</div>
								)}
								{isPosPinLocked && (
									<div className="space-y-2 border-t border-border pt-6 flex flex-col items-start">
										<p className="text-sm text-muted-foreground mt-2">
											{t("users.posPin.unlockDescription")}
										</p>
										<Button
											variant="warningOutline"
											className="w-full max-w-64"
											onClick={handleUnlockPosPin}
											disabled={pinActionLoading}
										>
											{t("users.posPin.unlockButton")}
										</Button>
									</div>
								)}
							</CardContent>
						</Card>
					</TabsContent>
				)}
			</Tabs>
		</div>
	);
};

export default EmployeeDetailPage;
